import React, { useEffect, useRef, useState } from "react";
import "./TemplateGenerator.css";
import { TEMPLATES } from "./templates";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { Download, OctagonAlert } from "lucide-react";
import { MyTooltip } from "../common/MyTooltip";
import htmlDocx from "html-docx-js/dist/html-docx";
import TemplateGeneratorCollectEmailModal from "./TemplateGeneratorCollectEmailModal";
import { apiSlackMessageTemplateDownload } from "../../services/SlackService";
import { set } from "lodash";
import { MAX } from "uuid";


const BREAK_BACKGROUND = {
    off: "white",
    on: "#FF543D",
}

const BREAK_COLOR = {
    off: "black",
    on: "white",
}

const MAX_AUTO_BREAKS_FIELDS = 6;


type TemplateGeneratorProps = {
};

export const TemplateGenerator = ({}: TemplateGeneratorProps) => {

    const { templateId } = useParams();
    
    const [templateObject, setTemplateObject] = useState<any>(null);
    const [template, setTemplate] = useState<string>("");
    const [fields, setFields] = useState<{ [key: string]: string }>({});
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [errorText, setErrorText] = useState<{ [key: string]: string }>({});
    const [fieldsOrdered, setFieldsOrdered] = useState<string[]>([]);
    const [currentBreak, setCurrenBreak] = useState<number>(0);
    const [firstFieldIndex, setFirstFieldIndex] = useState<number>(0);
    const [lastFieldIndex, setLastFieldIndex] = useState<number>(0);
    const [currentField, setCurrentField] = useState<string>("");

    const [collectEmail, setCollectEmail] = useState<boolean>(false);

    const templatePreviewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const selectedTemplate = TEMPLATES.find((template) => template.id === templateId);
        if (selectedTemplate) {
            setTemplateObject(selectedTemplate);
            setTemplate(selectedTemplate.template);
            extractFields(selectedTemplate.template);

            document.title = selectedTemplate.htmlTitle || "Winslow Template Generator";
        }
    }, [templateId]);

    useEffect(() => {

        // no breaks defined/broken
        if (!templateObject || !templateObject.breaks) {
            setFirstFieldIndex(0);
            setLastFieldIndex(100000);
            return;
        } 

        // empty array so automatically figure out the breaks
        if (templateObject.breaks.length === 0) {
            if (fieldsOrdered.length <= MAX_AUTO_BREAKS_FIELDS) {
                setFirstFieldIndex(0);
                setLastFieldIndex(100000);
                return;
            }

            setFirstFieldIndex(MAX_AUTO_BREAKS_FIELDS * currentBreak);
            setLastFieldIndex(MAX_AUTO_BREAKS_FIELDS * (currentBreak + 1));
            return;
        }
        
        // last one
        if (templateObject.breaks.length === currentBreak) {    
            setFirstFieldIndex(lastFieldIndex + 1)
            setLastFieldIndex(100000);
            return;
        }

        const breakFieldName = templateObject.breaks[currentBreak];
        const nextIndex = fieldsOrdered.indexOf(breakFieldName);

        // first one
        if (currentBreak === 0) {
            setFirstFieldIndex(0);
            setLastFieldIndex(nextIndex);
            return;
        }

        // in the middle
        const prevBreakFieldName = templateObject.breaks[currentBreak - 1];
        const prevFieldIndex = fieldsOrdered.indexOf(prevBreakFieldName);

        setFirstFieldIndex(prevFieldIndex + 1);
        setLastFieldIndex(nextIndex);

    }, [fieldsOrdered, currentBreak]);

    // const getBreakFieldIndexes = (breakNumber: number) : { firstIndex: number, lastIndex: number} | null => {
    // }


    // Extract fields in brackets (e.g., [Name]) from the template
    const extractFields = (text: string) => {
        const matches = text.match(/\[([^\]]+)\]/g) || [];
        const uniqueFields = Array.from(new Set(matches.map((match) => match.slice(1, -1))));
        setFieldsOrdered(uniqueFields);
        const updatedFields: { [key: string]: string } = {};
        uniqueFields.forEach((field) => {
            updatedFields[field] = fields[field] || ""; // Preserve existing values
        });
        setFields(updatedFields);
    };

    const handleFieldChange = (fieldName: string, value: string) => {
        setFields((prevFields) => ({
            ...prevFields,
            [fieldName]: value,
        }));

        if (fieldName.toLowerCase().includes("email")) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldName]: !emailRegex.test(value),
            }));
            setErrorText((prevErrorText) => ({
                ...prevErrorText,
                [fieldName]: "Invalid email",
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldName]: false,
            }));
            setErrorText((prevErrorText) => ({
                ...prevErrorText,
                [fieldName]: "",
            }));
        }
    };

    const handleSubmitEmail = async (email: string) => {
        setCollectEmail(false);

        handleDownload();

        const payload = {
            templateId: `${templateObject.id} - ${templateObject.title}`,
            email: email,
        };

        await apiSlackMessageTemplateDownload(payload)

    }

    const handleDownload = () => {
        if (!templatePreviewRef?.current) {
            return;
        }

        const templatePreview = templatePreviewRef.current;

 
        // // Get computed styles of the div element
        // const styles = window.getComputedStyle(templatePreviewRef.current);
    
        // // Convert computed styles to a style block
        // let styleContent = '';
        // for (let i = 0; i < styles.length; i++) {
        //     const styleName = styles[i];
        //     const styleValue = styles.getPropertyValue(styleName);
        //     styleContent += `${styleName}: ${styleValue};\n`;
        // }
    
        // // Create a style block and append it to the document head
        // const styleBlock = document.createElement('style');
        // styleBlock.innerHTML = `
        //     #dynamic-style-block {
        //     ${styleContent}
        //     }
        // `;

        // document.head.appendChild(styleBlock);        

        const style = 
        `
            <style>
                .preview {
                    padding: 24px;
                    overflow-wrap: break-word;
                    word-wrap: break-word;
                    white-space: pre-wrap;
                }
            </style>
        `;

        const previewHTML = templatePreview.innerHTML; // Get the text content of the preview

        // need to add <br/> tags for newlines
        const formattedPreviewHTML = previewHTML.replace(/\n/g, "<br/>");

        const htmlContent = `<html><head>${style}</head><body><div class="preview">${formattedPreviewHTML}</div></body></html>`; // Simple HTML structure with the text
        const docxFile = htmlDocx.asBlob(htmlContent);

        console.log("[TemplateGenerator] formattedPreviewHTML: ", formattedPreviewHTML);
        console.log("[TemplateGenerator] htmlContent: ", htmlContent);
        console.log("[TemplateGenerator] docxFile: ", docxFile);

        // Create a download link for the docx file
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(docxFile);
        downloadLink.download = `${templateObject.title || "document"}.docx`; // Filename for the DOCX file
        downloadLink.click();
    }


    const renderFieldInput = (fieldName: string, autoFocus = false) => {
        if (fieldName.toLowerCase().includes("date")) {
            return (
                <div 
                    className="template-generator-field-container"
                    onMouseDown={() => setCurrentField(fieldName)}                        

                >
                    <div 
                        className="template-generator-field-container-label"
                        style={(currentField === fieldName || fields[fieldName]) ? {} : {display: "none"}}
                    >
                        {fieldName}
                    </div>
                    <DatePicker
                        placeholderText={fieldName}
                        selected={fields[fieldName] ? new Date(fields[fieldName]) : null}
                        // dateFormat={"MM/dd/YYYY"}
                        onChange={(date) => {
                            handleFieldChange(fieldName, date?.toString() || "");
                        }}
                    
                        // className="date-picker-input"
                        wrapperClassName="date-picker-wrapper"
                        
                    />
                </div>
            );
        } else if (fieldName.toLowerCase().includes("email")) {
            return (
                <div 
                    className="template-generator-field-container"
                    onMouseDown={() => setCurrentField(fieldName)}
                >
                    <div 
                        className="template-generator-field-container-label"
                        style={(currentField === fieldName || fields[fieldName]) ? {} : {display: "none"}}
                    >
                        {fieldName}
                    </div>
                    <input
                        type="email"
                        placeholder={fieldName}
                        value={fields[fieldName] || ""}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        autoFocus={autoFocus}
                    />
                </div>
               
            );
        } else {
            return (
                <div 
                    className="template-generator-field-container"
                    onMouseDown={() => setCurrentField(fieldName)}
                >
                    <div 
                        className="template-generator-field-container-label"
                        style={(currentField === fieldName || fields[fieldName]) ? {} : {display: "none"}}
                        
                    >
                        {fieldName}
                    </div>
                    <input
                        type="text"
                        placeholder={currentField === fieldName ? "" : fieldName}
                        value={fields[fieldName] || ""}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        autoFocus={autoFocus}
                    />
                </div>
            );
        }
    };

    const renderPreview = () => {
        const parts = template.split(/(\[[^\]]+\])/g); // Split the template into text and bracketed fields

        // console.log("[TemplateGenerator] parts: ", parts);

        return (
            <div style={{display: "block"}}>
                {parts.map((part, index) => {
                    if (part.startsWith("[") && part.endsWith("]")) {
                        const fieldName = part.slice(1, -1); // Remove the brackets

                        // console.log("[TemplateGenerator] replacing fieldName: ", fieldName);
                        return (
                            <>
                                <span key={index} style={{ fontWeight: "bold" }}>
                                    {getFieldValue(fieldName)}
                                </span>
                                {/* <br/> */}
                            </>
                        );
                    }
                    return (
                        <>
                            <span key={index}>{part}</span>
                            {/* <br/> */}
                        </>
                    ) 
                })}
            </div>
        );
    }

    const renderBreakMap = (index: number, breakCount: number) => {
        console.log("[TemplateGenerator] renderBreakMap index: ", index);
        console.log("[TemplateGenerator] renderBreakMap breakCount: ", breakCount);

        if (index !== 0) {
            return (
                <>
                    <div 
                        className="template-generator-break-line"
                        style={{width: Math.floor(50/breakCount) + "%"}}
                    >
                        { }
                    </div>
                    <div 
                        className="template-generator-break-circle"
                        style={{ 
                            backgroundColor: index === currentBreak ? 
                                BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                            color: index === currentBreak ?
                                BREAK_COLOR.on : BREAK_COLOR.off,
                            border: "2px solid " + BREAK_BACKGROUND.on,
                        }}
                    >
                        { index + 1 }
                    </div>
                </>
            )
        } else {
            return (
                <div 
                    className="template-generator-break-circle"
                    style={{ 
                        backgroundColor: index === currentBreak ? 
                            BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                        color: index === currentBreak ?
                            BREAK_COLOR.on : BREAK_COLOR.off,
                        border: "2px solid " + BREAK_BACKGROUND.on,
                    }}
                >
                    { index + 1}
                </div>
            )

        }
       
    }

    const getFieldValue = (field: string) => {
        
        // console.log("[TemplateGenerator] getFieldValue field: ", field);
        
        let replacement = fields[field] || `[${field}]`;

        // console.log("[TemplateGenerator] getFieldValue replacement #1: ", replacement);

        const flc = field.toLowerCase();
        const isDate = flc.substring(0, 4) === "date" || flc.slice(-4) === "date" || flc.includes(" date ")

        if (isDate) {
            const date = fields[field] ? new Date(fields[field]) : new Date();
            replacement = date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        }

        // console.log("[TemplateGenerator] getFieldValue replacement final: ", replacement);

        return replacement;
    }

    const autoBreaksCount = Math.floor(fieldsOrdered.length / MAX_AUTO_BREAKS_FIELDS);
   
    console.log("[TemplateGenerator] templateObject: ", templateObject);
    console.log("[TemplateGenerator] fields: ", fields);
    // console.log("[TemplateGenerator] firstFieldIndex: ", firstFieldIndex);
    // console.log("[TemplateGenerator] lastFieldIndex: ", lastFieldIndex);
    console.log("[TemplateGenerator] currentBreak: ", currentBreak);

    return (
        <div className="template-generator">
            {templateObject && 
                <>
                    <div className="template-generator-editor">
                        <div className="template-generator-title">
                            {templateObject.title}
                            
                        </div>
                    
                        {templateObject.breaks && templateObject.breaks.length > 0 &&
                            <div className="template-generator-breaks">
                                { templateObject.breaks.map((_: any, index: number) => {
                                    // if (index !== 0) {
                                    //     return (
                                    //         <>
                                    //             <div 
                                    //                 className="template-generator-break-line"
                                    //                 style={{width: Math.floor(50/templateObject.breaks.length) + "%"}}
                                    //             >
                                    //                 { }
                                    //             </div>
                                    //             <div 
                                    //                 className="template-generator-break-circle"
                                    //                 style={{ 
                                    //                     backgroundColor: index === currentBreak ? 
                                    //                         BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                                    //                     color: index === currentBreak ?
                                    //                         BREAK_COLOR.on : BREAK_COLOR.off,
                                    //                     border: "2px solid " + BREAK_BACKGROUND.on,
                                    //                 }}
                                    //             >
                                    //                 { index + 1 }
                                    //             </div>
                                    //         </>
                                    //     )
                                    // } else {
                                    //     return (
                                    //         <div 
                                    //             className="template-generator-break-circle"
                                    //             style={{ 
                                    //                 backgroundColor: index === currentBreak ? 
                                    //                     BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                                    //                 color: index === currentBreak ?
                                    //                     BREAK_COLOR.on : BREAK_COLOR.off,
                                    //                 border: "2px solid " + BREAK_BACKGROUND.on,
                                    //             }}
                                    //         >
                                    //             { index + 1}
                                    //         </div>
                                    //     )

                                    // }
                                    return renderBreakMap(index, templateObject.breaks.length);
                                })}  
                                    <div 
                                        className="template-generator-break-line"
                                        style={{width: Math.floor(50/templateObject.breaks.length) + "%"}}
                                    >
                                        { }
                                    </div>
                                    <div 
                                        className="template-generator-break-circle"
                                        style={{ 
                                            backgroundColor: currentBreak === templateObject.breaks.length ? 
                                                BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                                            color: currentBreak === templateObject.breaks.length ?
                                                BREAK_COLOR.on : BREAK_COLOR.off,
                                            border: "2px solid " + BREAK_BACKGROUND.on,
                                        }}
                                    >
                                        { templateObject.breaks.length + 1 }
                                    </div>      
                                </div>
                        }
                        {templateObject.breaks && templateObject.breaks.length === 0 &&
                            <div className="template-generator-breaks">
                                {new Array(autoBreaksCount).fill(0).map((_: any, index) => {
                                    return renderBreakMap(index, autoBreaksCount);                                    
                                })}
                                    <div 
                                        className="template-generator-break-line"
                                        style={{width: Math.floor(50/autoBreaksCount) + "%"}}
                                    >
                                        { }
                                    </div>
                                    <div 
                                        className="template-generator-break-circle"
                                        style={{ 
                                            backgroundColor: currentBreak === autoBreaksCount ? 
                                                BREAK_BACKGROUND.on : BREAK_BACKGROUND.off,
                                            color: currentBreak === autoBreaksCount ?
                                                BREAK_COLOR.on : BREAK_COLOR.off,
                                            border: "2px solid " + BREAK_BACKGROUND.on,
                                        }}
                                    >
                                        { autoBreaksCount + 1 }
                                    </div>   
                            </div>
                        }
                        
                        <div className="template-generator-fields">
                            {Object.keys(fields).map((fieldName, index)=> {
                                if (index < firstFieldIndex || index > lastFieldIndex) {
                                    return (
                                        <>
                                        </>
                                    )
                                } else {
                                    return (
                                        <div key={fieldName} className="template-generator-field">
                                            {/* <label>{fieldName}:</label> */}
                                            {renderFieldInput(fieldName, index===0)}
                                            {fields[fieldName] && errors[fieldName] &&
                                                <MyTooltip 
                                                    title={
                                                        
                                                        <span
                                                            style={{
                                                            color: "var(--text-color-primary",
                                                            }}>
                                                            {errorText[fieldName]}
                                                        </span>
                                                    }
                                                >
                                            
                                                    <OctagonAlert color={"red"}/>
                                                </MyTooltip>
                                            }
                                        </div>
                                    )
                                }
                            })}

                        </div>
                        <div className="template-generator-editor-buttons">
                            {templateObject.breaks && templateObject.breaks.length > 0 &&
                                <>
                                    {templateObject.breaks && currentBreak > 0 &&
                                        <button onClick={() => setCurrenBreak((prev) => prev - 1)}>Prev</button>
                                    }
                                    {templateObject.breaks && currentBreak < templateObject.breaks.length &&
                                        <button onClick={() => setCurrenBreak((prev) => prev + 1)}>Next</button>
                                    }
                                    {templateObject.breaks && currentBreak === templateObject.breaks.length &&
                                        <button 
                                            id="button-download"
                                            onClick={() => setCollectEmail(true)}
                                        >
                                            <div className="button-download-content">
                                                <Download size={12} /> Download
                                            </div>
                                        </button>
                                    }
                                </>
                            }
                            {templateObject.breaks && templateObject.breaks.length === 0 &&
                                <>
                                    {currentBreak > 0 &&
                                        <button onClick={() => setCurrenBreak((prev) => prev - 1)}>Prev</button>
                                    }
                                    {currentBreak < autoBreaksCount &&
                                        <button onClick={() => setCurrenBreak((prev) => prev + 1)}>Next</button>
                                    }
                                    {currentBreak === autoBreaksCount &&
                                        <button 
                                            id="button-download"
                                            onClick={() => setCollectEmail(true)}
                                        >
                                            <div className="button-download-content">
                                                <Download size={12} /> Download
                                            </div>
                                        </button>
                                    }
                                </>
                            }
                        </div>
                       
                    </div>

                    <div className="template-generator-preview">
                        <div 
                            className="template-generator-preview-content"
                            ref={templatePreviewRef}
                        >
                                {renderPreview()}
                            </div>
                        <div 
                            className="template-generator-preview-download"
                            onClick={()=> setCollectEmail(true)}
                        >
                            <Download size={12} />
                            <span> download document</span>
                        </div>
                    </div>
                </>
            }

            {collectEmail &&
                <TemplateGeneratorCollectEmailModal 
                    isOpen={collectEmail}
                    onClose={() => setCollectEmail(false)}
                    onSubmit={handleSubmitEmail}
                />
        }   
        </div>
    );
};

